import Vue from 'vue';
import axios from 'axios';
import { Notify } from 'quasar';
import moment from 'moment';

import storage from 'src/util/storage';
import store from 'src/store';
import { wait } from 'src/util/helper';

// Setup default
axios.defaults.baseURL = `${process.env.VUE_APP_API_URL}/v1`;
axios.defaults.headers.common['X-Authorization'] = process.env.VUE_APP_XAUTH;
axios.defaults.headers.post['Content-Type'] = 'application/json';

// Setup authorization
axios.defaults.headers.common.Authorization = `JWT ${
	storage.get('auth.accessToken')
}`;

store.subscribe(({ type, payload }) => {
	if (type !== 'auth/SET_ACCESS_TOKEN') return;
	axios.defaults.headers.common.Authorization = `JWT ${payload}`;
});

axios.interceptors.request.use((config) => {
	if (store.state.admin.loggedInAs && config.root !== true) {
		config.baseURL += `/super/${store.state.admin.loggedInAs}`;
	}

	return config;
});

// Handle errors
axios.interceptors.response.use(null, async (error) => {
	if (!error.response && !error.request) {
		console.error(error);
		Notify.error('Es ist ein unerwarteter Fehler aufgetreten!');
		error.handled = true;
		throw error;
	}

	// Handle no connection
	if (!error.response && error.request) {
		error.handled = true;
		return handleDisconnect(error.config, error);
	}

	// Handle needs new token
	if (error.response.status === 401) {
		error.handled = true;
		return handleUnauthorized(error.config, error);
	}

	if (error.response.status === 403) {
		error.handled = true;
		Notify.error('Ihr Account befindet sich im Nur-Lesen Modus, bitte kontaktieren Sie den Support!');
	}

	if (error.response.status === 424) {
		error.handled = true;
		Notify.error('Der dahinterliegende Dienst steht zurzeit nicht zur Verfügung, bitte probieren Sie es später noch einmal!');
	}

	// A developer messed up the frontend validation
	if (
		error.response.status === 500
		&& error.response.data.error
		&& error.response.data.error === 'JoiValidationError'
	) {
		error.handled = true;
		console.error(error);

		if (
			error.response.data.details
			&& error.response.data.details.length
		) {
			const message = error.response.data.details[0].message;
			Notify.error(`Ungültige Eingabe: ${message}!`);
		} else {
			Notify.error('Die Eingaben entsprechen nicht der erforderten Form!');
		}
	} else if (error.response.status >= 500) {
		error.handled = true;
		console.error(error);
		Notify.error('Es ist ein unerwarteter interner Fehler aufgetreten!');
	}

	throw error;
});

Vue.prototype.$axios = axios;

let dismissDisconnectError = null;
async function handleDisconnect(config, error) {
	console.error(error);

	if (!config.disconnect) {
		config.disconnect = {
			test: 0,
		};
	}

	if (config.disconnect.test === 3) {
		if (dismissDisconnectError) dismissDisconnectError();
		dismissDisconnectError = Notify.error(
			'Die Verbindung zum Server wurde unterbrochen, '
			+ 'bitte probieren Sie es später noch einmal!'
		);
	}

	config.disconnect.test++;
	await wait(moment.duration(
		config.disconnect.test > 3 ? 5 : 2,
		'seconds',
	));

	const request = axios(config);
	request.then(() => {
		if (dismissDisconnectError) dismissDisconnectError();
		dismissDisconnectError = null;
	});
	return request;
}

async function handleUnauthorized(config, error) {
	if (config.unauthorizedRetried) {
		throw error;
	}

	await store.dispatch('auth/refreshToken');
	const accessToken = store.state.auth.accessToken;

	if (!accessToken) {
		throw error;
	}

	config.unauthorizedRetried = true;
	config.headers.Authorization = `JWT ${accessToken}`;

	return axios(config);
}
